/**
 * Tiny drop-in replacements for the subset of lodash helpers used in
 * `@ocap/client`. Lodash's per-helper modules pulled ~180 files / ~30 KB
 * into the browser bundle; this module provides equivalent behavior for
 * the call sites we actually hit.
 *
 * Scope & quirks:
 *   - `get` / `set` accept dot-separated paths ('a.b.c') or array paths
 *     (['a','b','c']). Bracket notation ('a[0]') is NOT supported — the
 *     two real call sites in extension.js use dot notation only.
 *   - `camelCase` handles `_`/`-` word separators on ASCII identifiers;
 *     used here only on `send_XxxTx`-style transaction names, where a
 *     single-regex replace is sufficient.
 *   - `pick` / `omit` are shallow, mirror the lodash contract used by the
 *     headers / signature-sanitization call sites.
 */

function camelCase(str) {
  if (str == null) return '';
  return String(str).replace(/[_-](\w)/g, (_, c) => c.toUpperCase());
}

function pick(obj, keys) {
  const result = {};
  if (!obj) return result;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key];
    }
  }
  return result;
}

function omit(obj, keys) {
  const result = {};
  if (!obj) return result;
  const deny = new Set(keys);
  for (const key of Object.keys(obj)) {
    if (!deny.has(key)) {
      result[key] = obj[key];
    }
  }
  return result;
}

function toPath(path) {
  if (Array.isArray(path)) return path;
  return String(path).split('.');
}

function get(obj, path, defaultValue) {
  if (obj == null) return defaultValue;
  const parts = toPath(path);
  let current = obj;
  for (const part of parts) {
    if (current == null) return defaultValue;
    current = current[part];
  }
  return current === undefined ? defaultValue : current;
}

function set(obj, path, value) {
  if (obj == null) return obj;
  const parts = toPath(path);
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (current[part] == null || typeof current[part] !== 'object') {
      current[part] = {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
  return obj;
}

module.exports = { camelCase, pick, omit, get, set };
